import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! We'll get back to you shortly.");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Location",
      details: ["7885 6th St #201", "Burnaby, BC V3N 3N4"],
      link: "https://maps.google.com/?q=7885+6th+St+%23201,+Burnaby,+BC+V3N+3N4"
    },
    {
      icon: Phone,
      title: "Phone",
      details: ["(604) 553-4000", "Direct billing available"],
      link: "tel:+16045534000"
    },
    {
      icon: Mail,
      title: "Email",
      details: ["appointments@bc-ice.ca", "We respond within 24 hours"],
      link: "mailto:appointments@bc-ice.ca"
    },
    {
      icon: Clock,
      title: "Hours",
      details: ["Mon-Fri: 9AM - 6PM", "Sat: By appointment"],
    },
  ];

  const services = [
    "Physiotherapy",
    "Massage Therapy",
    "Kinesiology",
    "Acupuncture",
    "HeadWorks (TMD)",
  ];

  return (
    <section
      id="contact"
      className="py-20 md:py-28 bg-[#F9FAFB]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#0F4C3A] font-semibold text-sm uppercase tracking-wider">
            Get In Touch
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            Start Your <span className="text-[#0F4C3A]">Recovery Journey</span>
          </h2>
          <p className="mt-6 text-lg text-gray-600">
            Have questions? Ready to book? Contact us today and let our team 
            help you get back to feeling your best.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Info Cards */}
            <div className="grid sm:grid-cols-2 gap-6">
              {contactInfo.map((item, index) => (
                <a
                  key={index}
                  href={item.link || "#"}
                  target={item.link?.startsWith("http") ? "_blank" : undefined}
                  rel={item.link?.startsWith("http") ? "noopener noreferrer" : undefined}
                  className={`bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow ${item.link ? "cursor-pointer" : ""}`}
                  data-testid={`contact-info-${item.title.toLowerCase()}`}
                >
                  <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#0F4C3A]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  {item.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 text-sm">
                      {detail}
                    </p>
                  ))}
                </a>
              ))}
            </div>

            {/* Map Embed */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2604.5!2d-122.9515!3d49.2267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5486761e9e9e9e9e%3A0x1234567890abcdef!2s7885%206th%20St%20%23201%2C%20Burnaby%2C%20BC%20V3N%203N4!5e0!3m2!1sen!2sca!4v1704300000000!5m2!1sen!2sca"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BC-ICE Location - 7885 6th St #201, Burnaby, BC"
              ></iframe>
            </div>

            {/* Quick CTA */}
            <div className="bg-[#0F4C3A] text-white p-8 rounded-2xl">
              <h3 className="text-xl font-bold mb-2">Prefer to Book Online?</h3>
              <p className="text-white/80 mb-6">
                Skip the form and book your appointment directly through our online booking system.
              </p>
              <a
                href="https://bc-ice.janeapp.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-book-btn"
              >
                <Button className="w-full bg-white text-[#0F4C3A] hover:bg-gray-100 rounded-full py-4 font-semibold">
                  Book Online Now
                </Button>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-lg">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#D1FAE5] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#0F4C3A]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Message Sent!
                </h3>
                <p className="text-gray-600 mb-6">
                  Thank you for reaching out. We'll get back to you within 24 hours.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-2 border-[#0F4C3A] text-[#0F4C3A] rounded-full"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Send Us a Message
                  </h3>
                  <p className="text-gray-600">
                    Fill out the form below and we'll get back to you shortly.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="rounded-xl border-gray-200 focus:border-[#0F4C3A] focus:ring-[#0F4C3A]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="rounded-xl border-gray-200 focus:border-[#0F4C3A] focus:ring-[#0F4C3A]"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(604) 555-0123"
                      className="rounded-xl border-gray-200 focus:border-[#0F4C3A] focus:ring-[#0F4C3A]"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Interest
                    </label>
                    <Select onValueChange={handleServiceChange} value={formData.service}>
                      <SelectTrigger className="rounded-xl border-gray-200" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your condition or what you'd like help with..."
                    rows={5}
                    className="rounded-xl border-gray-200 focus:border-[#0F4C3A] focus:ring-[#0F4C3A]"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full py-4 font-semibold shadow-lg hover:shadow-xl transition-all"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
